/**
 * The shared pager.
 *
 * Three pages in this app had each grown their own Previous/Next block
 * with slightly different wording and slightly different rules about when
 * it appeared. This is one of them, so a fix lands everywhere.
 *
 * WHY IT SHOWS EVEN ON A SINGLE PAGE: the hidden-until-page-2 version
 * leaves you unable to change the page size until you already have too
 * many rows, and hides the total - "1-1 of 1" is a useful thing to see,
 * because it confirms the filter did what you asked. It hides itself only
 * when there is genuinely nothing to page through.
 *
 * WHY A WINDOW OF NUMBERS, NOT ALL OF THEM: forty page buttons is not
 * navigation, it is wallpaper. First, last, and the three around the
 * current page cover how people actually move through a list; the gaps
 * are marked so nobody thinks page 4 follows page 2.
 */
export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size control entirely. */
  onPageSizeChange?: (pageSize: number) => void;
  /** The API caps pageSize at 100, so nothing here may exceed it. */
  pageSizeOptions?: number[];
  /** "contacts", "entries", "documents" - plural, lower case. */
  noun?: string;
}

/** [1, '…', 4, 5, 6, '…', 20] - first, last and a window around current. */
function pageWindow(page: number, pages: number): Array<number | 'gap'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);

  const out: Array<number | 'gap'> = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push('gap');
  for (let p = from; p <= to; p += 1) out.push(p);
  if (to < pages - 1) out.push('gap');
  out.push(pages);
  return out;
}

export function Pagination({
  page, pageSize, total, onPageChange, onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  noun = 'items',
}: PaginationProps) {
  if (total === 0) return null;

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <nav
      aria-label="Pagination"
      className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500"
    >
      <span>
        Showing <span className="font-medium text-slate-700">{first}-{last}</span>
        {' '}of <span className="font-medium text-slate-700">{total}</span> {noun}
      </span>

      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5">
            <span className="text-slate-400">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs outline-none transition focus:border-brand-500"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}

        {pages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              aria-label="Previous page"
              className="rounded-lg border border-slate-300 px-2.5 py-1 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>

            {pageWindow(page, pages).map((entry, i) =>
              entry === 'gap' ? (
                <span key={`gap-${i}`} aria-hidden="true" className="px-1 text-slate-300">…</span>
              ) : (
                <button
                  key={entry}
                  type="button"
                  onClick={() => onPageChange(entry)}
                  aria-label={`Page ${entry}`}
                  aria-current={entry === page ? 'page' : undefined}
                  className={[
                    'min-w-7 rounded-lg border px-2 py-1 transition',
                    entry === page
                      ? 'border-brand-500 bg-brand-50 font-semibold text-brand-700'
                      : 'border-slate-300 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {entry}
                </button>
              ),
            )}

            <button
              type="button"
              disabled={page >= pages}
              onClick={() => onPageChange(page + 1)}
              aria-label="Next page"
              className="rounded-lg border border-slate-300 px-2.5 py-1 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
